/**
 * SourceControlOnboardingTooltip Component
 * EVI-405: Source Control Onboarding Tooltip
 * EVI-457: Restyle tooltip + add post-trial variant
 *
 * A tooltip shown to users on their first Evidence experience (first-load)
 * or after their trial expires (post-trial).
 * - First-load: "Try now" CTA → opens source control popover
 * - Post-trial: "Upgrade to Pro" CTA → opens upgrade modal
 * - Paid Pro users: No tooltip shown
 */

import { hasSeenPostTrialTooltipAtom } from '@/atoms/chatAtoms';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryUser } from '@heidi/workspace/data';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom } from 'jotai';
import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { track } from '@/lib/analytics/track';


/**
 * Hook to detect if any dialog/modal is open in the DOM
 * Uses MutationObserver to watch for dialogs with role="dialog"
 */
const useHasOpenDialog = () => {
  const [hasOpenDialog, setHasOpenDialog] = useState(false);

  useEffect(() => {
    const checkForDialogs = () => {
      // Check for any element with role="dialog" (Radix dialogs use this)
      const hasDialog = document.querySelector('[role="dialog"]') !== null;
      setHasOpenDialog(hasDialog);
    };

    // Initial check
    checkForDialogs();

    // Watch for DOM changes to detect when dialogs open/close
    const observer = new MutationObserver(checkForDialogs);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return hasOpenDialog;
};

interface SourceControlOnboardingTooltipProps {
  /** The trigger element (typically the SourcesButton) */
  children: ReactNode;
  /** Callback when user clicks the CTA button (first-load: try source control) */
  onTrySourceControl?: () => void;
  /** Callback when user clicks upgrade CTA (post-trial) */
  onUpgrade?: () => void;
  /** Callback when tooltip open state changes (for parent to disable popover) */
  onTooltipOpenChange?: (isOpen: boolean) => void;
  /** Override the open state (for testing) */
  forceOpen?: boolean;
  /** Whether the tooltip should be allowed to show (parent can block it if needed) */
  shouldShow?: boolean;
}

export const SourceControlOnboardingTooltip = ({
  children,
  onTrySourceControl,
  onUpgrade,
  onTooltipOpenChange,
  forceOpen,
  shouldShow = true,
}: SourceControlOnboardingTooltipProps) => {
  const { t } = useTranslations();
  const { data: hasSeenOnboarding, isLoading: isUserLoading } = useQueryUser(
    (user) => user?.has_seen_evidence_source_control_coachmark ?? false
  );
  const { mutate: updateUser } = useMutateUpdateUser({ enableInvalidation: false });
  const [hasSeenPostTrial, setHasSeenPostTrial] = useAtom(hasSeenPostTrialTooltipAtom);
  const hasModalOpen = useHasOpenDialog();
  const [isOpen, setIsOpen] = useState(false);
  const hasDismissedRef = useRef(false);

  // Get billing info to determine which tooltip variant to show
  const { hasEvidencePro, isInFreeTrial, hasTrialExpired } = useEvidenceBillingInfo();

  // Paid Pro users (not in trial) should never see tooltips
  const isPaidPro = hasEvidencePro && !isInFreeTrial;

  // Determine which tooltip variant to show
  const tooltipVariant = useMemo(() => {
    if (isUserLoading) return null;
    if (isPaidPro) return null;
    if (hasTrialExpired && !hasSeenPostTrial) return 'post-trial' as const;
    if (!hasTrialExpired && !hasSeenOnboarding) return 'first-load' as const;
    return null;
  }, [isUserLoading, isPaidPro, hasTrialExpired, hasSeenPostTrial, hasSeenOnboarding]);

  // Notify parent when tooltip open state changes
  useEffect(() => {
    onTooltipOpenChange?.(isOpen);
  }, [isOpen, onTooltipOpenChange]);

  useEffect(() => {
    if (isOpen) {
      track({ options: { amplitude: false },
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.SourceControlOnboardingTooltip },
      });
    }
  }, [isOpen]);

  // Show tooltip after a short delay if conditions are met
  useEffect(() => {
    if (forceOpen) return;
    if (!tooltipVariant) return;
    if (hasDismissedRef.current) return;
    if (hasModalOpen) {
      setIsOpen(false);
      return;
    }
    if (!shouldShow) {
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(() => {
      if (!hasDismissedRef.current && !hasModalOpen && shouldShow) {
        setIsOpen(true);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [tooltipVariant, forceOpen, hasModalOpen, shouldShow]);

  const handleDismiss = useCallback(() => {
    hasDismissedRef.current = true;
    setIsOpen(false);
    if (tooltipVariant === 'post-trial') {
      setHasSeenPostTrial(true);
    } else {
      updateUser({ has_seen_evidence_source_control_coachmark: true });
    }
  }, [tooltipVariant, updateUser, setHasSeenPostTrial]);

  const handleCtaClick = useCallback(() => {
    handleDismiss();

    setTimeout(() => {
      if (tooltipVariant === 'post-trial') {
        onUpgrade?.();
      } else {
        onTrySourceControl?.();
      }
    }, 200);
  }, [handleDismiss, tooltipVariant, onUpgrade, onTrySourceControl]);

  // If no tooltip should be shown and not forcing open, just render children
  if (!tooltipVariant && !forceOpen) {
    return <>{children}</>;
  }

  // Get translation keys based on variant
  const translationPrefix =
    tooltipVariant === 'post-trial' ? 'sources.postTrial' : 'sources.onboarding';

  return (
    <TooltipProvider>
      <Tooltip open={isOpen || forceOpen}>
        <TooltipTrigger asChild>
          <div>{children}</div>
        </TooltipTrigger>

        <TooltipContent
          side="top"
          align="start"
          sideOffset={8}
          className="w-64 rounded-lg border-none bg-callout-background p-3 shadow-lg"
        >
          {/* Header with title and close button */}
          <div className="flex items-start justify-between gap-2">
            <TypographyP2 className="font-medium text-text-primary-invert">
              {t(`${translationPrefix}.title`)}
            </TypographyP2>
            <button
              type="button"
              onClick={handleDismiss}
              className="flex size-4 shrink-0 items-center justify-center rounded transition-colors hover:bg-white/10"
              aria-label={t('common.close')}
            >
              <X className="size-3 text-text-primary-invert" strokeWidth={2} />
            </button>
          </div>

          {/* Description */}
          <TypographyCaption className="mt-2 text-text-primary-invert">
            {t(`${translationPrefix}.description`)}
          </TypographyCaption>

          {/* CTA Button - positioned at bottom-right */}
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              size="xs"
              variant="secondary"
              className="bg-background-primary-invert text-text-primary hover:bg-background-primary-invert/80"
              onClick={handleCtaClick}
            >
              {t(`${translationPrefix}.cta`)}
            </Button>
          </div>

          <TooltipArrow className="fill-callout-background" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
